"use client";

import * as z from "zod";

import { PasswordInputFormField } from "@/components/form/password-input-form-field";
import { Form } from "@/components/shadcn/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { Button } from "@/components/shadcn/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/shadcn/card";
import { authClient } from "@/lib/auth/client";
import { buildPasswordComplexitySchema } from "@/lib/auth/password-policy";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

export default function PasswordChangeForm() {
    const tAccount = useTranslations("account");
    const tAuth = useTranslations("auth");
    const router = useRouter();
    const passwordRequirements = {
        minLength: tAuth("passwordMin"),
        uppercase: tAuth("passwordUppercase"),
        lowercase: tAuth("passwordLowercase"),
        number: tAuth("passwordNumber"),
    };
    const formSchema = z
        .object({
            currentPassword: z
                .string({
                    required_error: tAccount("currentPasswordRequired"),
                })
                .min(1, tAccount("currentPasswordRequired")),
            newPassword: z
                .string({
                    required_error: tAccount("newPasswordRequired"),
                })
                .min(1, tAccount("newPasswordRequired"))
                .and(buildPasswordComplexitySchema(passwordRequirements)),
            reNewPassword: z
                .string({
                    required_error: tAccount("newPasswordRepeatRequired"),
                })
                .min(1, tAccount("newPasswordRepeatRequired")),
        })
        .refine((values) => values.newPassword === values.reNewPassword, {
            message: tAuth("passwordsMustMatch"),
            path: ["reNewPassword"],
        });
    type InputType = z.infer<typeof formSchema>;

    const form = useForm<InputType>({
        resolver: zodResolver(formSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            reNewPassword: "",
        },
    });

    const onSubmit = async (values: InputType) => {
        const { error } = await authClient.changePassword({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
        });
        if (error) {
            if (error.code === "INVALID_PASSWORD") {
                form.setError("newPassword", { message: error.message });
            }

            toast.error(tAccount("passwordUpdateFailed"), {
                description:
                    error.code === "INVALID_PASSWORD"
                        ? error.message
                        : error.message || tAccount("checkCurrentPassword"),
            });
            return;
        }
        toast.success(tAccount("passwordUpdated"), {
            description: tAccount("passwordUpdatedDesc"),
        });
        router.push("/kullanici/ayarlar");
    };

    return (
        <Card>
            <CardHeader className="sr-only">
                <CardTitle>{tAccount("passwordChangeTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
                <Form {...form}>
                    <form
                        className="flex flex-col gap-6"
                        onSubmit={form.handleSubmit(onSubmit)}
                    >
                        <div className="grid gap-x-8 gap-y-4 lg:grid-cols-2">
                            <PasswordInputFormField
                                control={form.control}
                                name="currentPassword"
                                label={tAccount("currentPassword")}
                                autoComplete="current-password"
                                required
                            />
                            <PasswordInputFormField
                                control={form.control}
                                name="newPassword"
                                label={tAccount("newPassword")}
                                autoComplete="new-password"
                                required
                                passwordRequirements={passwordRequirements}
                            />
                            <PasswordInputFormField
                                control={form.control}
                                name="reNewPassword"
                                label={tAccount("newPasswordRepeat")}
                                autoComplete="new-password"
                                required
                            />
                        </div>
                        <div className="flex justify-end">
                            <Button type="submit">
                                {tAccount("confirmPasswordChange")}
                            </Button>
                        </div>
                    </form>
                </Form>
            </CardContent>
        </Card>
    );
}
